// Resizable two-pane layout. The handle is a focusable separator (WAI-ARIA window
// splitter): arrow keys resize by 2%, Home/End jump to the limits.
import { useCallback, useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";

interface SplitPaneProps {
  start: ReactNode;
  end: ReactNode;
  /** Size of the start pane in percent. */
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  label: string;
  className?: string;
}

export function SplitPane({ start, end, value, onChange, min = 45, max = 75, label, className }: SplitPaneProps) {
  const container = useRef<HTMLDivElement>(null);
  const clamp = useCallback((v: number) => Math.min(max, Math.max(min, v)), [min, max]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const el = container.current;
    if (!el) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    const move = (ev: globalThis.PointerEvent) => onChange(clamp(((ev.clientX - rect.left) / rect.width) * 100));
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null;
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = value - 2;
    else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = value + 2;
    else if (e.key === "Home") next = min;
    else if (e.key === "End") next = max;
    if (next !== null) {
      e.preventDefault();
      onChange(clamp(next));
    }
  };

  return (
    <div className={["split", className ?? ""].join(" ")} ref={container}>
      <div className="split-pane" style={{ flexBasis: `${value}%`, flexGrow: 0, flexShrink: 1 }}>
        {start}
      </div>
      <div
        className="split-handle"
        role="separator"
        tabIndex={0}
        aria-orientation="vertical"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={Math.round(value)}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
      />
      <div className="split-pane" style={{ flex: 1 }}>
        {end}
      </div>
    </div>
  );
}
